import { addDays, differenceInCalendarDays, format, nextMonday, set, startOfDay } from 'date-fns';

import type { InboxPullRequest } from '../types';

/** The viewer's snoozes: when each snoozed PR returns, in epoch ms, by snooze key. */
export type InboxSnoozes = Readonly<Record<string, number>>;

/** Case-insensitive, as GitHub names are, since a card keeps the case its source gave it. */
export function getSnoozeKey(owner: string, repo: string, number: number): string {
  return `${owner}/${repo}#${number}`.toLowerCase();
}

export function getPrSnoozeKey(pr: InboxPullRequest): string {
  return getSnoozeKey(pr.repository.owner, pr.repository.name, pr.number);
}

/** The snoozes that have not ended at `now`. One that has returns its PR to its own section. */
export function getActiveSnoozes(snoozes: InboxSnoozes, now: number): InboxSnoozes {
  return Object.fromEntries(Object.entries(snoozes).filter(([, until]) => until > now));
}

/** When the PR's snooze in `snoozes` ends, in epoch ms. */
export function getSnoozedUntil(snoozes: InboxSnoozes, pr: InboxPullRequest): number | undefined {
  return snoozes[getPrSnoozeKey(pr)];
}

export type SnoozeOption = {
  id: 'later-today' | 'tomorrow' | 'next-week';
  label: string;
  until: Date;
};

const at = (day: Date, hours: number) =>
  set(day, { hours, minutes: 0, seconds: 0, milliseconds: 0 });

/**
 * The preset times in the viewer's local time: Later today at 6:00 PM, offered before 5:00 PM,
 * Tomorrow at 9:00 AM, and Next week on the coming Monday at 9:00 AM. On a Sunday that Monday is
 * tomorrow, so Next week is left out.
 */
export function getSnoozeOptions(now: Date): SnoozeOption[] {
  const tomorrow = at(addDays(now, 1), 9);
  const nextWeek = at(nextMonday(now), 9);
  return [
    ...(now.getHours() < 17
      ? [{ id: 'later-today' as const, label: 'Later today', until: at(now, 18) }]
      : []),
    { id: 'tomorrow', label: 'Tomorrow', until: tomorrow },
    ...(nextWeek.getTime() === tomorrow.getTime()
      ? []
      : [{ id: 'next-week' as const, label: 'Next week', until: nextWeek }]),
  ];
}

/** A day picked on the calendar snoozes until 9:00 AM that day. */
export function getSnoozeUntilDay(day: Date): Date {
  return at(day, 9);
}

/** The calendar's days: from the first whose 9:00 AM is still ahead, for a year. */
export function getSnoozeDays(now: Date): { fromDate: Date; toDate: Date } {
  const today = startOfDay(now);
  return {
    fromDate: now < getSnoozeUntilDay(today) ? today : addDays(today, 1),
    toDate: addDays(today, 365),
  };
}

const TIME = 'h:mm a';

/** When a snooze ends: "6:00 PM" today, "Tue 9:00 AM" within a week, "Oct 20, 9:00 AM" later. */
export function formatSnoozeTime(until: Date, now: Date): string {
  const days = differenceInCalendarDays(until, now);
  return format(until, days === 0 ? TIME : days < 7 ? `EEE ${TIME}` : `MMM d, ${TIME}`);
}

/** The same in a sentence: "6:00 PM", "tomorrow, 9:00 AM", "Mon, 9:00 AM", "Oct 20, 9:00 AM". */
export function describeSnoozeTime(until: Date, now: Date): string {
  const days = differenceInCalendarDays(until, now);
  if (days === 1) {
    return `tomorrow, ${format(until, TIME)}`;
  }
  return format(until, days === 0 ? TIME : days < 7 ? `EEE, ${TIME}` : `MMM d, ${TIME}`);
}
